import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Button, Card, Table, Select, Form, Row, Col, Typography, Space, Tag, Flex } from 'antd';
import { PrinterOutlined, DollarOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextField,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  OutlinedInput,
  Typography as MuiTypography,
  FormHelperText,
  Container,
  Avatar
} from '@mui/material';
import {
  AttachMoney as CashIcon,
  AccountBalance as BankIcon,
  Receipt as CheckIcon,
  CreditCard as CardIcon,
  AssignmentReturn as RefundIcon,
  Close as CloseIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import HttpsIcon from '@mui/icons-material/Https';

// Import Components
import { generateCreditNotePDF } from '../utils/generateCreditNotePDF';
import generateThermalRefundReceipt from '../utils/generateThermalRefundReceipt';
import { CreditNoteNotFound, CreditNoteError } from './CreditNoteStatus';
import LoadingInformation from '../../Components/LoadingInformation';

const { Title, Text } = Typography;
const { Option } = Select;

// Función helper para formatear moneda
function formatCurrency(value) {
  if (value === null || value === undefined) return 'RD$0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return 'RD$0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// Función helper para formatear fecha
function formatDate(dateString) {
  if (!dateString) return 'No registrada';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function toMySQLDatetime(dateString) {
  const date = new Date(dateString);
  // Ajustar para la zona horaria local para evitar diferencias UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Agrupa servicios y suma todos los pagos hechos a un mismo selectedServiceID, incluyendo pagos al originalSelectedServiceID si aplica
const groupServicesBySelectedServiceID = (invoiceDetails, debitNotes = [], creditNotes = []) => {
  const grouped = {};

  // Agrupa todos los detalles por selectedServiceID
  invoiceDetails
    .filter(detail => detail.originalSelectedServiceID === null)
    .forEach(detail => {
      const key = detail.selectedServiceID;
      if (!grouped[key]) {
        grouped[key] = {
          ...detail,
          amount: 0, // Inicializa el monto pagado
          pagos: [], // Para depuración
        };
      }
      grouped[key].amount += parseNumber(detail.amount) || 0;
      grouped[key].pagos.push(parseNumber(detail.amount) || 0);
    });

  // Ahora calcula pendiente, estado, etc.
  return Object.values(grouped).map(detail => {
    let pagadoOriginal = 0;

    // Calcular el total del servicio
    const totalServicio = parseNumber(detail.servicePrice) * parseNumber(detail.serviceQty) * (1 - (parseFloat(detail.discountApplied) || 0) / 100);

    // El pagado total debe incluir pagos al original si es corrección
    const pagadoTotal = (detail.correctionType === 'debit' && detail.originalSelectedServiceID)
      ? pagadoOriginal
      : parseFloat(detail.amount) || 0;

    // El pendiente también debe considerar el pagado total
    const pendiente = (detail.correctionType === 'debit' && detail.originalSelectedServiceID)
      ? Math.max(totalServicio - pagadoOriginal, 0)
      : Math.max(totalServicio - (parseFloat(detail.amount) || 0), 0);

    // Estado visual
    let estado = 'pendiente';
    if (detail.paymentStatus === 'pagado') estado = 'pagado';
    else if (pendiente === 0) estado = 'pagado';

    return {
      ...detail,
      pendiente,
      pagado: pagadoTotal,
      estado,
      totalServicio
    };
  });
};

function CreditNoteView() {
  const { creditNoteId } = useParams();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [permissionError, setPermissionError] = useState(false);

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [creditNote, setCreditNote] = useState(null);
  const [creditNoteDetails, setCreditNoteDetails] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [refundReceipts, setRefundReceipts] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  // Estados para diálogo de devolución
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundForm] = Form.useForm();
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedServicesDetailed, setSelectedServicesDetailed] = useState([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [reference, setReference] = useState('');
  const [bank, setBank] = useState('');
  const [banksOptions, setBanksOptions] = useState([]);
  const [printerConfig, setPrinterConfig] = useState({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
  const [configLoading, setConfigLoading] = useState(false);

  // Estados para extensión
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [extensionChecked, setExtensionChecked] = useState(false);

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    setPermissionError(false);
    setLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  // Detectar cambio de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Función para cargar datos de la nota de crédito
  const fetchCreditNoteData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        messageApi.error({
          content: 'Token de autenticación no encontrado. Por favor inicie sesión nuevamente.',
          duration: 5
        }).then(() => {
          navigate('/sign-in');
        });
      }

      if (!creditNoteId) {
        messageApi.error('ID de nota de crédito no proporcionado');
        return;
      }

      // Obtener datos de la nota de crédito
      const response = await fetch(`https://dprincess-training.servehttp.com/credit-notes/list-note/${creditNoteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 404) {
        setNotFound(true);
        return;
      }

      if (!response.ok) {
        throw new Error('Error al obtener datos de la nota de crédito');
      }

      const data = await response.json();

      setCreditNote(data.creditNote);
      setCreditNoteDetails(data.creditNoteDetails);
      const invoiceDetails = groupServicesBySelectedServiceID(data.invoiceData.invoiceAllDetails);
      data.invoiceData.invoiceDetails = invoiceDetails; // Actualiza los detalles agrupados
      setInvoiceData(data.invoiceData);
      setRefundReceipts(data.refundReceipts || []);

    } catch (error) {
      console.error('Error fetching credit note data:', error);
      messageApi.error('Error al cargar los datos de la nota de crédito');
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://dprincess-training.servehttp.com/billing-services/bankAccounts/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBanksOptions(data);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar las cuentas bancarias, por favor intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al cargar las cuentas bancarias.',
      });
    }
  };

  // Cargar datos de la nota de crédito
  useEffect(() => {
    if (creditNoteId) {
      fetchCreditNoteData();
    }
  }, [creditNoteId]);

  useEffect(() => {
    fetchCreditNoteData();
  }, []);

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  // Manejar impresión del PDF
  const handlePrintCreditNote = async () => {
    if (!creditNote || !invoiceData || !creditNoteDetails) {
      messageApi.error('No hay datos suficientes para generar el PDF');
      return;
    }

    try {
      messageApi.info('Generando PDF de la nota de crédito...');

      const pdf = generateCreditNotePDF(creditNote, invoiceData, creditNoteDetails);

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newWindow = window.open(pdfUrl, '_blank');

      if (newWindow) {
        newWindow.onload = () => {
          setTimeout(() => {
            newWindow.print();
          }, 500);
        };

        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 30000);
      } else {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `Nota_Crédito_${creditNote.creditNoteNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfUrl);

        messageApi.info('Se ha descargado el PDF de la nota de crédito');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      messageApi.error('Error al generar el PDF de la nota de crédito');
    }
  };

  // Abrir diálogo de devolución
  const openRefundModal = () => {
    // Filtrar servicios que tienen monto pendiente por devolver
    const pendingServices = creditNoteDetails.filter(detail =>
      detail.paymentStatus === 'pendiente' && detail.amount > 0
    );

    if (pendingServices.length === 0) {
      messageApi.info('No hay servicios pendientes de devolución en esta nota de crédito');
      return;
    }

    setSelectedServices([]);
    setSelectedServicesDetailed([]);
    setRefundAmount(0);
    setPaymentMethod('efectivo');
    refundForm.resetFields();
    setRefundDialogOpen(true);
  };

  // Manejar selección de servicios para devolución
  const handleServiceSelection = (selectedServiceIds) => {

    setSelectedServices(selectedServiceIds);

    // Calcular monto total a devolver
    let totalAmount = 0;
    let amounts = [];

    selectedServiceIds.forEach((serviceId, index) => {
      const detail = creditNoteDetails.find(d => d.creditNoteDetailID === serviceId);

      if (detail) {
        // Asegurar que convertimos a número correctamente
        let amount = 0;
        if (typeof detail.amount === 'string') {
          amount = parseFloat(detail.amount.replace(/,/g, '')) || 0;
        } else if (typeof detail.amount === 'number') {
          amount = detail.amount;
        }
        amounts.push(amount);
        totalAmount += amount;
      }
    });
    setRefundAmount(totalAmount);
    const selectedServicesDetailed = selectedServiceIds.map((serviceId, idx) => {
      const detail = creditNoteDetails.find(d => d.creditNoteDetailID === serviceId);
      const selectedServices = invoiceData.invoiceDetails.find(d => d.selectedServiceID === Number(detail.selectedServiceID));
      return {
        selectedServiceID: selectedServices.selectedServiceID,
        amount: amounts[idx] || 0,
        creditNoteDetailID: detail ? detail.creditNoteDetailID : null,
        concept: selectedServices ? 'Reembolso por servicio ' + selectedServices.serviceName + '. Debido a ' + detail.concept : "Reembolso por servicio"
      };
    });
    setSelectedServicesDetailed(selectedServicesDetailed);
  };

  // Fetch printer configuration from C# agent
  const fetchAgentPrinterConfiguration = async () => {
    if (isMobile) return; // No hacer nada si es móvil
    setConfigLoading(true);
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      console.log('Extensión no instalada para fetchAgentPrinterConfiguration');
      messageApi.error('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para obtener configuración de impresora.');
      setConfigLoading(false);
      return;
    }

    try {
      console.log('Enviando proxyRequest para /printer-config via extensión');
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/printer-config',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response && response.success) {
        try {
          let config;
          if (typeof response.data === 'string') {
            config = JSON.parse(response.data);
          } else {
            config = response.data;
          }
          console.log("Agent Printer Configuration via extension:", config);
          setPrinterConfig(config);
          if (config.IsPrinterSelected) {
            messageApi.success(`Configuración de impresora '${config.SelectedPrinterName}' (${config.SuggestedCharsPerLine} car.) obtenida.`);
          } else {
            messageApi.info(`Agente no tiene impresora predeterminada. Usando ancho ${config.SuggestedCharsPerLine} car.`);
          }
        } catch (parseError) {
          console.error('Error al parsear configuración de impresora:', parseError);
          messageApi.error('Error al procesar la configuración de impresora recibida.');
          setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
        }
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        console.error(`Failed to get printer config via extension: ${errorMsg}`);
        messageApi.error("Fallo al obtener configuración de impresora del agente via extensión.");
        setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
      }
    } catch (error) {
      console.error("Error en fetchAgentPrinterConfiguration via extensión:", error);
      messageApi.error("Error de conexión al contactar agente para configuración de impresora via extensión.");
      setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
    } finally {
      setConfigLoading(false);
    }
  };

  // Manejar impresión térmica directa
  const handleDirectThermalPrinter = async (escposData) => {
    // Chequear la extensión en tiempo real antes de imprimir
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      console.log('Extensión no instalada para handleDirectThermalPrinter');
      return { success: false, message: 'La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para imprimir.' };
    }

    try {
      // Asegurar que tenemos configuración de impresora antes de imprimir
      const printerName = printerConfig?.IsPrinterSelected ? printerConfig.SelectedPrinterName : null;

      console.log('Enviando proxyRequest para /print via extensión');
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/print',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          data: escposData,
          printerName: printerName,
          printType: 'RAW'
        }
      });

      if (response && response.success) {
        const printerUsed = printerName || 'impresora predeterminada';
        return { success: true, message: `Documento enviado a ${printerUsed}: ${response.data}` };
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        console.error('Error en respuesta de la extensión:', errorMsg);
        return { success: false, message: `Error de impresión: ${errorMsg}` };
      }
    } catch (error) {
      console.error('Error al enviar el recibo térmico via extensión:', error);
      return { success: false, message: 'Error de conexión con la impresora. Verifique que esté conectada y el agente en ejecución.' };
    }
  }

  // Generar e imprimir recibo de reembolso
  const generateAndPrintRefundReceipt = async (refundResult) => {
    try {
      Swal.fire({
        title: 'Imprimiendo recibo de reembolso...',
        allowOutsideClick: false,
        text: 'Por favor, espere mientras se procesa la solicitud.',
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      // Verificar si tenemos configuración de impresora, si no, obtenerla
      if (!printerConfig) {
        await fetchAgentPrinterConfiguration();
      }

      // Usar la configuración de la impresora obtenida del agente C#
      const paperWidthCharsProvided = printerConfig?.SuggestedCharsPerLine || 48; // Usar configuración de impresora o fallback

      // Enriquecer los detalles del reembolso con información de servicios
      const enrichedRefundReceiptDetails = refundResult.refundReceiptDetails.map(detail => {
        const creditDetail = creditNoteDetails?.find(cd =>
          cd.creditNoteDetailID === detail.creditNoteDetailID
        );
        const serviceDetail = invoiceData?.invoiceDetails?.find(sd =>
          sd.selectedServiceID === detail.selectedServiceID
        );

        return {
          ...detail,
          serviceName: creditDetail?.serviceName || serviceDetail?.serviceName || 'Servicio desconocido',
          originalPrice: creditDetail?.servicePrice || serviceDetail?.servicePrice || '0',
          serviceQty: serviceDetail?.serviceQty || 1,
          concept: detail.concept || `Reembolso por ${creditDetail?.serviceName || 'servicio'}`,
          // Información adicional si es un cambio de servicio
          isServiceChange: !!creditDetail?.newServiceName,
          newServiceName: creditDetail?.newServiceName,
          newServicePrice: creditDetail?.newServicePrice,
          changeReason: creditDetail?.concept
        };
      });

      // Crear la estructura refundData con los datos disponibles
      const refundData = {
        refundReceipt: {
          ...refundResult.refundReceipt,
          // Información adicional del usuario actual si está disponible
          registeredByUserName: invoiceData?.employeeName || 'Usuario no identificado'
        },
        refundReceiptDetails: enrichedRefundReceiptDetails,
        invoiceData: {
          ...invoiceData,
          // Asegurar que tenemos toda la información necesaria
          totalRefunded: parseNumber(refundResult.refundReceipt.totalAmountReturned),
          refundDate: refundResult.refundReceipt.returnDate
        },
        creditNote: {
          ...creditNote,
          // Información adicional sobre la nota de crédito
          reasonForCredit: creditNote.reason || 'Corrección de servicios'
        },
        creditNoteDetails: creditNoteDetails.map(detail => ({
          ...detail,
          // Enriquecer con información de cambios si existe
          hasServiceChange: !!detail.newServiceName,
          priceDifference: detail.servicePrice && detail.newServicePrice ?
            parseNumber(detail.servicePrice) - parseNumber(detail.newServicePrice) : 0
        }))
      };

      // Generar el recibo térmico
      const thermalReceiptData = await generateThermalRefundReceipt(refundData, paperWidthCharsProvided);

      if (!thermalReceiptData) {
        messageApi.error('Error al generar los datos del recibo térmico');
        Swal.close();
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron generar los datos del recibo térmico. Por favor, recargue la página e imprima directamente.',
        });
        return;
      }

      const receiptPrintResult = await handleDirectThermalPrinter(thermalReceiptData);
      if (!receiptPrintResult?.success) {
        // Error de impresión, pero reembolso ya registrado
        Swal.close();
        Swal.fire({
          icon: 'warning',
          title: 'Error de Impresión',
          text: 'Reembolso registrado exitosamente, pero hubo un problema al imprimir. Verifique la conexión de la impresora.',
        });
        messageApi.open({
          type: 'warning',
          content: 'Reembolso registrado exitosamente, pero hubo un problema al imprimir. Verifique la conexión de la impresora.',
          duration: 5,
        });
        setRefundDialogOpen(false)
      } else {
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Impresión Exitosa',
          text: 'Pago registrado y documentos impresos exitosamente.',
        });
        messageApi.open({
          type: 'success',
          content: 'Pago registrado y documentos impresos exitosamente.',
          duration: 3,
        });
        setRefundDialogOpen(false)
      }

    } catch (error) {
      Swal.close();
      console.error('Error generando recibo de reembolso:', error);
      messageApi.error('Error al generar el recibo de reembolso');
      await Swal.fire({
        icon: 'info',
        title: 'Fallo al imprimir recibo de reembolso',
        text: 'No se pudo imprimir el recibo de reembolso. Por favor, inténtelo de nuevo más tarde.',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  // Reimprimir un recibo térmico específico
  const reprintThermalReceipt = async (receipt) => {
    try {
      Swal.fire({
        title: 'Reimprimiendo recibo térmico...',
        allowOutsideClick: false,
        text: 'Por favor, espere mientras se procesa la solicitud.',
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Verificar si tenemos configuración de impresora, si no, obtenerla
      if (!printerConfig) {
        await fetchAgentPrinterConfiguration();
      }

      // Usar la configuración de la impresora obtenida del agente C#
      const paperWidthCharsProvided = printerConfig?.SuggestedCharsPerLine || 48;

      // Crear estructura de datos para el recibo existente
      const enrichedRefundReceiptDetails = receipt.details?.map(detail => {
        const creditDetail = creditNoteDetails?.find(cd =>
          cd.creditNoteDetailID === detail.creditNoteDetailID
        );
        const serviceDetail = invoiceData?.invoiceDetails?.find(sd =>
          sd.selectedServiceID === detail.selectedServiceID
        );

        return {
          ...detail,
          serviceName: creditDetail?.serviceName || serviceDetail?.serviceName || 'Servicio desconocido',
          originalPrice: creditDetail?.servicePrice || serviceDetail?.servicePrice || '0',
          serviceQty: serviceDetail?.serviceQty || 1,
          concept: detail.concept || `Reembolso por ${creditDetail?.serviceName || 'servicio'}`,
          // Información adicional si es un cambio de servicio
          isServiceChange: !!creditDetail?.newServiceName,
          newServiceName: creditDetail?.newServiceName,
          newServicePrice: creditDetail?.newServicePrice,
          changeReason: creditDetail?.concept,
          // Mapear desde la estructura de details a la estructura esperada
          amountReturned: detail.amountReturned,
          selectedServiceID: detail.selectedServiceID,
          creditNoteDetailID: detail.creditNoteDetailID
        };
      }) || [];

      // Crear la estructura refundData para reimprimir
      const refundData = {
        refundReceipt: {
          refundReceiptID: receipt.refundReceiptID,
          receiptDocumentNumber: receipt.receiptDocumentNumber,
          returnDate: receipt.returnDate,
          totalAmountReturned: receipt.totalAmountReturned,
          paymentMethod: receipt.paymentMethod,
          referenceNumber: receipt.referenceNumber,
          notes: receipt.notes,
          registeredByUserName: invoiceData?.employeeName || 'Usuario no identificado'
        },
        refundReceiptDetails: enrichedRefundReceiptDetails,
        invoiceData: {
          ...invoiceData,
          totalRefunded: parseNumber(receipt.totalAmountReturned),
          refundDate: receipt.returnDate
        },
        creditNote: {
          ...creditNote,
          reasonForCredit: creditNote.reason || 'Corrección de servicios'
        },
        creditNoteDetails: creditNoteDetails.map(detail => ({
          ...detail,
          hasServiceChange: !!detail.newServiceName,
          priceDifference: detail.servicePrice && detail.newServicePrice ?
            parseNumber(detail.servicePrice) - parseNumber(detail.newServicePrice) : 0
        }))
      };

      // Generar el recibo térmico
      const thermalReceiptData = await generateThermalRefundReceipt(refundData, paperWidthCharsProvided);

      if (!thermalReceiptData) {
        messageApi.error('Error al generar los datos del recibo térmico');
        Swal.close();
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron generar los datos del recibo térmico para reimprimir.',
        });
        return;
      }

      const receiptPrintResult = await handleDirectThermalPrinter(thermalReceiptData);
      if (!receiptPrintResult?.success) {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error de Impresión',
          text: 'Hubo un problema al reimprimir el recibo. Verifique la conexión de la impresora.',
        });
        messageApi.open({
          type: 'error',
          content: 'Error al reimprimir el recibo térmico. Verifique la conexión de la impresora.',
          duration: 5,
        });
      } else {
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Reimpresión Exitosa',
          text: `Recibo ${receipt.receiptDocumentNumber || 'sin número'} reimpreso exitosamente.`,
        });
        messageApi.open({
          type: 'success',
          content: 'Recibo térmico reimpreso exitosamente.',
          duration: 3,
        });
      }

    } catch (error) {
      Swal.close();
      console.error('Error reimprimiendo recibo térmico:', error);
      messageApi.error('Error al reimprimir el recibo térmico');
      await Swal.fire({
        icon: 'error',
        title: 'Fallo al reimprimir recibo',
        text: 'No se pudo reimprimir el recibo térmico. Por favor, inténtelo de nuevo más tarde.',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  // Procesar devolución
  const handleRefundSubmit = async () => {
    if (selectedServices.length === 0) {
      messageApi.error('Debe seleccionar al menos un servicio para procesar la devolución');
      return;
    }

    if (refundAmount <= 0) {
      messageApi.error('El monto de devolución debe ser mayor a cero');
      return;
    }
    const refundTotalAmount = parseNumber(refundAmount);

    const isConfirmed = await Swal.fire({
      title: '¿Confirmar devolución?',
      text: `Se procesará una devolución por ${formatCurrency(refundTotalAmount)}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar'
    });

    if (!isConfirmed.isConfirmed) return;

    try {
      setLoading(true);
      Swal.fire({
        title: 'Procesando devolución...',
        text: 'Por favor, espere mientras se procesa la devolución.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      const token = localStorage.getItem('token');
      const refundReceipt = {
        creditNoteID: creditNote.creditNoteID,
        invoiceID: invoiceData.invoiceID,
        returnDate: toMySQLDatetime(new Date()),
        totalAmountReturned: refundTotalAmount,
        paymentMethod: paymentMethod,
        referenceNumber: reference || null,
        bank: bank || null,
        notes: `Devolución de nota de crédito ${creditNote.creditNoteNumber}`,
        createdAt: toMySQLDatetime(new Date())
      }
      const refundReceiptDetails = selectedServicesDetailed.map(service => ({
        selectedServiceID: service.selectedServiceID,
        amountReturned: service.amount,
        concept: service.concept,
        creditNoteDetailID: service.creditNoteDetailID,
        createdAt: toMySQLDatetime(new Date())
      }));
      const creditNoteDetails = selectedServicesDetailed.map(service => ({
        creditNoteDetailID: service.creditNoteDetailID,
        paymentStatus: 'pagado'
      }));
      const cashFlows = selectedServicesDetailed.map(service => ({
        selectedServiceID: service.selectedServiceID,
        amount: service.amount,
        paymentMethod: paymentMethod,
        creationDate: toMySQLDatetime(new Date()),
        description: service.concept,
        clientID: invoiceData.clientID,
        flowType: 'egreso'
      }));
      const postData = {
        creditNoteID: creditNote.creditNoteID,
        invoiceID: invoiceData.invoiceID,
        creditNoteDetails,
        refundReceipt: refundReceipt,
        refundReceiptDetails: refundReceiptDetails,
        cashFlows: cashFlows
      }

      const response = await fetch(`https://dprincess-training.servehttp.com/credit-notes/refund/${creditNote.creditNoteID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        Swal.close();
        await Swal.fire({
          icon: 'error',
          title: 'Error al procesar la devolución',
          text: errorData.error || 'Error al procesar la devolución',
          confirmButtonText: 'Aceptar'
        });
        throw new Error(errorData.error || 'Error al procesar la devolución');
      }

      const result = await response.json();

      Swal.close();
      messageApi.success('Devolución procesada exitosamente');

      await Swal.fire({
        title: 'Devolución exitosa',
        text: `Se ha registrado la devolución por ${formatCurrency(refundAmount)}`,
        icon: 'success',
        confirmButtonText: 'Aceptar'
      });

      // Generar e imprimir recibo de reembolso
      await generateAndPrintRefundReceipt(result);

      setRefundDialogOpen(false);
      await fetchCreditNoteData(); // Recargar datos

    } catch (error) {
      console.error('Error processing refund:', error);
      Swal.close();
      messageApi.error(error.message || 'Error al procesar la devolución');
      Swal.fire({
        title: 'Error',
        text: error.message || 'Error al procesar la devolución',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función helper para enviar mensajes a la extensión (con ID dinámico o sin ID)
  const sendToExtension = async (message) => {
    console.log('window.thermalPrinterProxy exists:', !!window.thermalPrinterProxy);
    console.log('window.thermalPrinterProxy value:', window.thermalPrinterProxy);

    if (window.thermalPrinterProxy) {
      // Usar función global inyectada por la extensión
      console.log('Usando función global thermalPrinterProxy');
      const response = await window.thermalPrinterProxy.sendMessage(message);

      // Verificar si la respuesta es una instancia de Response (de fetch)
      if (response instanceof Response) {
        try {
          if (response.ok) {
            const data = await response.text();
            return { success: true, data };
          } else {
            const error = await response.text();
            return { success: false, error };
          }
        } catch (e) {
          console.error('Error al leer el body de la Response:', e);
          return { success: false, error: 'Error al procesar la respuesta de la extensión: ' + e.message };
        }
      } else {
        // Asumir que es un objeto con { success, data, error }
        return response;
      }
    } else {
      // Obtener ID dinámico de localStorage
      const extensionId = localStorage.getItem('thermalPrinterExtensionId');
      console.log('Extension ID from localStorage:', extensionId);

      if (extensionId) {
        console.log('Usando chrome.runtime.sendMessage con ID dinámico:', extensionId);
        return new Promise((resolve, reject) => {
          chrome.runtime.sendMessage(
            extensionId,
            message,
            (response) => {
              if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message));
              } else {
                resolve(response);
              }
            }
          );
        });
      } else {
        messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
        return { success: false, error: 'No se encontró el ID de la extensión en localStorage.' };
      }
    }
  };

  // Función para chequear la extensión en tiempo real (actualizada)
  const checkExtensionRealTime = async () => {
    console.log('Iniciando checkExtensionRealTime...');
    if (window.thermalPrinterProxy) {
      console.log('Función global thermalPrinterProxy disponible');
      return true;
    }
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      console.log('Chrome runtime no disponible');
      return false;
    }

    // Obtener ID dinámico de localStorage
    const extensionId = localStorage.getItem('thermalPrinterExtensionId');
    console.log('Extension ID from localStorage:', extensionId);

    if (!extensionId) {
      console.log('No hay ID en localStorage, extensión no disponible');
      return false;
    }

    try {
      console.log('Enviando ping a extensión con ID dinámico:', extensionId);
      const response = await new Promise((resolve, reject) => {
        chrome.runtime.sendMessage(
          extensionId,
          { action: 'ping' },
          (response) => {
            if (chrome.runtime.lastError) {
              reject(new Error(chrome.runtime.lastError.message));
            } else {
              resolve(response);
            }
          }
        );
      });
      console.log('Respuesta de extensión al ping:', response);
      return response && response.success;
    } catch (error) {
      console.error('Error verificando extensión en tiempo real:', error);
      return false;
    }
  };

  // Verificar extensión al montar el componente
  useEffect(() => {
    const checkExtension = async () => {
      if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
        setExtensionInstalled(false);
        setExtensionChecked(true);
        messageApi.error('La extensión no está instalada o no se puede acceder.');
        return;
      }

      // Obtener ID dinámico de localStorage
      const extensionId = localStorage.getItem('thermalPrinterExtensionId');
      console.log('Extension ID from localStorage en useEffect:', extensionId);

      if (!extensionId) {
        setExtensionInstalled(false);
        setExtensionChecked(true);
        messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
        return;
      }

      try {
        const response = await new Promise((resolve, reject) => {
          chrome.runtime.sendMessage(
            extensionId,
            { action: 'ping' },
            (response) => {
              if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message));
              } else {
                resolve(response);
              }
            }
          );
        });

        setExtensionInstalled(response && response.success);
      } catch (error) {
        console.error('Error verificando extensión:', error);
        messageApi.error('Error al verificar la extensión: ' + error.message);
        setExtensionInstalled(false);
      } finally {
        setExtensionChecked(true);
      }
    };

    checkExtension();
  }, []);

  useEffect(() => {
    if (!isMobile && extensionChecked && extensionInstalled) {
      fetchAgentPrinterConfiguration(); // Fetch printer configuration on mount
    }
  }, [extensionChecked, extensionInstalled]);

  // Configuración de columnas para la tabla de detalles (Desktop)
  const detailsColumns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Servicio',
      dataIndex: 'serviceName',
      key: 'serviceName',
      render: (_, record) => {
        const serviceInfo = invoiceData?.invoiceDetails?.find(
          detail => detail.selectedServiceID === record.selectedServiceID
        );
        return serviceInfo?.serviceName || 'Servicio no encontrado';
      }
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const serviceInfo = invoiceData?.invoiceDetails?.find(
          detail => detail.selectedServiceID === record.selectedServiceID
        );
        return serviceInfo?.serviceQty || 1;
      }
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const serviceInfo = invoiceData?.invoiceDetails?.find(
          detail => detail.selectedServiceID === record.selectedServiceID
        );
        return formatCurrency(serviceInfo?.servicePrice || 0);
      }
    },
    {
      title: 'Motivo',
      dataIndex: 'concept',
      key: 'concept'
    },
    {
      title: 'Monto Acreditado',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Estado del Crédito',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 140,
      align: 'center',
      render: (status) => (
        <Tag color={status === 'pendiente' ? 'orange' : 'green'} icon={status === 'pendiente' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}>
          {status === 'pendiente' ? 'Pendiente' : 'Aplicado'}
        </Tag>
      )
    }
  ];

  // Configuración de columnas para recibos de devolución
  const refundReceiptsColumns = [
    {
      title: 'Número de Recibo',
      dataIndex: 'receiptDocumentNumber',
      key: 'receiptDocumentNumber',
      width: 150,
      render: (receiptNumber) => receiptNumber || 'No disponible'
    },
    {
      title: 'Fecha',
      dataIndex: 'returnDate',
      key: 'returnDate',
      width: 180,
      render: (date) => formatDate(date)
    },
    {
      title: 'Método de Pago',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 120,
      render: (method) => method?.charAt(0).toUpperCase() + method?.slice(1) || 'No especificado'
    },
    {
      title: 'Monto Devuelto',
      dataIndex: 'totalAmountReturned',
      key: 'totalAmountReturned',
      width: 140,
      align: 'right',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Notas',
      dataIndex: 'notes',
      key: 'notes'
    },
    {
      title: 'Referencia',
      dataIndex: 'referenceNumber',
      key: 'referenceNumber',
      width: 120,
      render: (ref) => ref || 'N/A'
    },
    {
      title: 'Banco',
      dataIndex: 'bank',
      key: 'bank',
      render: (bank) => banksOptions.find(b => b.id === bank)?.bank || 'N/A'
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <IconButton
          onClick={() => reprintThermalReceipt(record)}
          size="small"
          title="Imprimir recibo térmico"
          sx={{
            color: '#1890ff',
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(24, 144, 255, 0.2)',
            },
            width: '32px',
            height: '32px'
          }}
        >
          <PrintIcon fontSize="small" />
        </IconButton>
      )
    }
  ];

  if (loading) {
    return <LoadingInformation />;
  };

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  if (notFound) {
    return <CreditNoteNotFound />;
  };

  if (error) {
    return <CreditNoteError onRetry={fetchCreditNoteData} />;
  };

  return (
    <div
      style={{
        padding: isMobile ? 0 : 0,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%'
      }}
    >
      {contextHolder}

      <Box sx={{ padding: '24px', backgroundColor: '#e9e8e8ff' }}>
        {/* Header con botones de acción */}
        <Card
          style={{ marginBottom: '24px' }}
          styles={{ body: { padding: isMobile ? '16px' : '24px' } }}
        >
          <Flex
            justify="space-between"
            align={isMobile ? "flex-start" : "center"}
            wrap="wrap"
            gap="middle"
            vertical={isMobile}
          >
            <Flex align="center" gap="middle">
              <Title
                level={isMobile ? 3 : 2}
                style={{
                  margin: 0,
                  color: '#1890ff',
                  fontSize: isMobile ? '18px' : '24px'
                }}
              >
                Nota de Crédito {creditNote.creditNoteNumber}
              </Title>
            </Flex>

            <Flex gap="middle" vertical={isMobile} style={{ width: isMobile ? '100%' : 'auto' }}>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrintCreditNote}
                size={isMobile ? "middle" : "large"}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Imprimir
              </Button>
              <Button
                type="default"
                icon={<DollarOutlined />}
                onClick={openRefundModal}
                disabled={
                  creditNote.paymentStatus === 'aplicado' ||
                  (refundReceipts && refundReceipts.length > 0) ||
                  creditNoteDetails.every(detail => detail.paymentStatus === 'pagado')
                }
                size={isMobile ? "middle" : "large"}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Procesar Devolución
              </Button>
            </Flex>
          </Flex>
        </Card>
        {/* Información general de la nota de crédito */}
        <Card
          title={
            <Space>
              <FileTextOutlined />
              Información General
            </Space>
          }
          style={{ marginBottom: '24px' }}
          styles={{
            header: {
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #d9d9d9'
            }
          }}
        >
          {isMobile ? (
            // Vista móvil
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Flex justify="space-between" align="center" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Número:</Text>
                <Text>{creditNote.creditNoteNumber}</Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Factura relacionada:</Text>
                <Text>{invoiceData?.invoiceNumber}</Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Cliente:</Text>
                <Text>{invoiceData?.clientName}</Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Fecha de emisión:</Text>
                <Text>{formatDate(creditNote.creditNoteDate)}</Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Monto total:</Text>
                <Text style={{ color: '#52c41a', fontWeight: 600, fontSize: '16px' }}>
                  {formatCurrency(creditNote.amount)}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Estado:</Text>
                <Tag
                  color={creditNote.paymentStatus === 'pendiente' ? 'orange' : 'green'}
                  icon={creditNote.paymentStatus === 'pendiente' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                >
                  {creditNote.paymentStatus === 'pendiente' ? 'Pendiente' : 'Aplicado'}
                </Tag>
              </Flex>
              <Flex justify="space-between" align="flex-start" style={{
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Text strong>Motivo:</Text>
                <Text style={{ textAlign: 'right', maxWidth: '60%' }}>{creditNote.reason}</Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{ padding: '8px 0' }}>
                <Text strong>Emitida por:</Text>
                <Text>{invoiceData?.employeeName}</Text>
              </Flex>
            </div>
          ) : (
            // Vista desktop
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Número de nota de crédito:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {creditNote.creditNoteNumber}
                  </Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Factura relacionada:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {invoiceData?.invoiceNumber}
                  </Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Cliente:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {invoiceData?.clientName}
                  </Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Fecha de emisión:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {formatDate(creditNote.creditNoteDate)}
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Monto total acreditado:
                  </Text>
                  <Text style={{
                    fontSize: '16px',
                    color: '#52c41a',
                    fontWeight: 600
                  }}>
                    {formatCurrency(creditNote.amount)}
                  </Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Estado:
                  </Text>
                  <Tag
                    color={creditNote.paymentStatus === 'pendiente' ? 'orange' : 'green'}
                    icon={creditNote.paymentStatus === 'pendiente' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                  >
                    {creditNote.paymentStatus === 'pendiente' ? 'Pendiente de aplicación' : 'Aplicado'}
                  </Tag>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Motivo general:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {creditNote.reason}
                  </Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Emitida por:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#262626' }}>
                    {invoiceData?.employeeName}
                  </Text>
                </div>
              </Col>
            </Row>
          )}
        </Card>

        {/* Detalles de servicios afectados */}
        <Card
          title="Detalles de Servicios Afectados"
          style={{ marginBottom: '24px' }}
          styles={{
            header: {
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #d9d9d9'
            }
          }}
        >
          {isMobile ? (
            // Vista móvil - Cards para cada servicio
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {creditNoteDetails.map((detail, index) => {
                const serviceInfo = invoiceData?.invoiceDetails?.find(
                  service => service.selectedServiceID === detail.selectedServiceID
                );

                return (
                  <Card
                    key={index}
                    size="small"
                    style={{
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px'
                    }}
                    styles={{ body: { padding: '12px' } }}
                  >
                    <Flex justify="space-between" align="flex-start" style={{ marginBottom: '12px' }}>
                      <Text strong style={{ flex: 1 }}>
                        {serviceInfo?.serviceName || 'Servicio no encontrado'}
                      </Text>
                      <Tag
                        color={detail.paymentStatus === 'pendiente' ? 'orange' : 'green'}
                        icon={detail.paymentStatus === 'pendiente' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                      >
                        {detail.paymentStatus === 'pendiente' ? 'Pendiente' : 'Aplicado'}
                      </Tag>
                    </Flex>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">Cantidad:</Text>
                        <Text>{serviceInfo?.serviceQty || 1}</Text>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">Precio unitario:</Text>
                        <Text>{formatCurrency(serviceInfo?.servicePrice || 0)}</Text>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">Motivo:</Text>
                        <Text style={{ textAlign: 'right', maxWidth: '60%' }}>{detail.concept}</Text>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">Monto acreditado:</Text>
                        <Text strong style={{ color: '#52c41a', fontWeight: 600 }}>
                          {formatCurrency(detail.amount)}
                        </Text>
                      </Flex>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            // Vista desktop - Tabla
            <Table
              columns={detailsColumns}
              dataSource={creditNoteDetails}
              rowKey="creditNoteDetailID"
              pagination={false}
              size="middle"
              style={{
                backgroundColor: '#fafafa'
              }}
            />
          )}
        </Card>

        {/* Recibos de devolución */}
        {refundReceipts.length > 0 && (
          <Card
            title="Recibos de Devolución"
            style={{ marginBottom: '24px' }}
            styles={{
              header: {
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #d9d9d9'
              }
            }}
          >
            {isMobile ? (
              // Vista móvil para recibos
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {refundReceipts.map((receipt, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px'
                    }}
                    styles={{ body: { padding: '12px' } }}
                  >
                    <Flex justify="space-between" align="flex-start" style={{ marginBottom: '12px' }}>
                      <div>
                        <Text strong style={{ display: 'block' }}>
                          {receipt.receiptDocumentNumber || 'Recibo sin número'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatDate(receipt.returnDate)}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconButton
                          onClick={() => reprintThermalReceipt(receipt)}
                          size="small"
                          title="Imprimir recibo térmico"
                          sx={{
                            color: '#1890ff',
                            backgroundColor: 'rgba(24, 144, 255, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(24, 144, 255, 0.2)',
                            },
                            width: '28px',
                            height: '28px'
                          }}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        <Text style={{ color: '#52c41a', fontWeight: 600 }}>
                          {formatCurrency(receipt.totalAmountReturned)}
                        </Text>
                      </div>
                    </Flex>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">Método:</Text>
                        <Text>
                          {receipt.paymentMethod?.charAt(0).toUpperCase() + receipt.paymentMethod?.slice(1)}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">Referencia:</Text>
                        <Text>{receipt.referenceNumber || 'N/A'}</Text>
                      </Flex>
                      <Flex justify="space-between" align="flex-start">
                        <Text type="secondary">Notas:</Text>
                        <Text style={{ textAlign: 'right', maxWidth: '60%' }}>
                          {receipt.notes || 'Sin notas'}
                        </Text>
                      </Flex>
                      {receipt.details && receipt.details.length > 0 && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                          <Text type="secondary" style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                            Detalles ({receipt.details.length} servicios):
                          </Text>
                          {receipt.details.map((detail, detailIndex) => (
                            <div key={detailIndex} style={{ marginBottom: '4px' }}>
                              <Text style={{ fontSize: '12px' }}>
                                • {formatCurrency(detail.amountReturned)} - {detail.concept}
                              </Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              // Vista desktop para recibos
              <Table
                columns={refundReceiptsColumns}
                dataSource={refundReceipts}
                rowKey="refundReceiptID"
                pagination={false}
                size="middle"
                expandable={{
                  expandedRowRender: (record) => (
                    <div style={{ margin: 0 }}>
                      <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                        Detalles del reembolso:
                      </Text>
                      {record.details && record.details.length > 0 ? (
                        <Table
                          columns={[
                            {
                              title: 'Servicio',
                              dataIndex: 'selectedServiceID',
                              key: 'selectedServiceID',
                              render: (serviceId) => {
                                const detail = creditNoteDetails.find(d => d.creditNoteDetailID === serviceId);
                                return detail?.serviceName || `Servicio ID: ${serviceId}`;
                              }
                            },
                            {
                              title: 'Concepto',
                              dataIndex: 'concept',
                              key: 'concept'
                            },
                            {
                              title: 'Monto Devuelto',
                              dataIndex: 'amountReturned',
                              key: 'amountReturned',
                              align: 'right',
                              render: (amount) => formatCurrency(amount)
                            }
                          ]}
                          dataSource={record.details}
                          rowKey="refundReceiptDetailID"
                          pagination={false}
                          size="small"
                        />
                      ) : (
                        <Text type="secondary">No hay detalles disponibles</Text>
                      )}
                    </div>
                  ),
                  expandRowByClick: true,
                  rowExpandable: (record) => record.details && record.details.length > 0,
                }}
              />
            )}
          </Card>
        )}
      </Box>

      {/* Diálogo para procesar devolución */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{
          backgroundColor: 'rgba(34, 197, 94, 0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RefundIcon sx={{ color: '#22c55e', mr: 1 }} />
            <MuiTypography variant="h6" sx={{ color: '#059669', fontWeight: 600 }}>
              Procesar Devolución
            </MuiTypography>
          </Box>
          <IconButton
            onClick={() => setRefundDialogOpen(false)}
            aria-label="close"
            sx={{ color: '#22c55e' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="services-select-label">Servicios a devolver</InputLabel>
              <MuiSelect
                labelId="services-select-label"
                multiple
                value={selectedServices}
                onChange={(e) => handleServiceSelection(e.target.value)}
                input={<OutlinedInput label="Servicios a devolver" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const detail = creditNoteDetails.find(d => d.creditNoteDetailID === value);
                      const serviceInfo = invoiceData?.invoiceDetails?.find(
                        service => service.selectedServiceID === detail?.selectedServiceID
                      );
                      return (
                        <Chip
                          key={value}
                          label={`${serviceInfo?.serviceName} - ${formatCurrency(detail?.amount || 0)}`}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {creditNoteDetails
                  .filter(detail => detail.paymentStatus === 'pendiente' && detail.amount > 0)
                  .map(detail => {
                    const serviceInfo = invoiceData?.invoiceDetails?.find(
                      service => service.selectedServiceID === detail.selectedServiceID
                    );
                    return (
                      <MenuItem key={detail.creditNoteDetailID} value={detail.creditNoteDetailID}>
                        <Box>
                          <MuiTypography variant="body2" sx={{ fontWeight: 500 }}>
                            {serviceInfo?.serviceName || 'Servicio no encontrado'}
                          </MuiTypography>
                          <MuiTypography variant="caption" sx={{ color: 'text.secondary' }}>
                            Motivo: {detail.concept} - Monto: {formatCurrency(detail.amount)}
                          </MuiTypography>
                        </Box>
                      </MenuItem>
                    );
                  })
                }
              </MuiSelect>
            </FormControl>

            <TextField
              fullWidth
              label="Monto total a devolver"
              value={refundAmount.toLocaleString('es-DO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              disabled
              sx={{
                mb: 2,
                '& .MuiInputBase-input': {
                  fontWeight: 600,
                  color: '#22c55e',
                  textAlign: 'right',
                  fontSize: '16px'
                },
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(34, 197, 94, 0.05)',
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
              }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="payment-method-label">Método de pago</InputLabel>
              <MuiSelect
                labelId="payment-method-label"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                label="Método de pago"
              >
                <MenuItem value="efectivo">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CashIcon sx={{ mr: 1, color: '#22c55e' }} />
                    Efectivo
                  </Box>
                </MenuItem>
                <MenuItem value="transferencia">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BankIcon sx={{ mr: 1, color: '#3b82f6' }} />
                    Transferencia Bancaria
                  </Box>
                </MenuItem>
                <MenuItem value="cheque">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckIcon sx={{ mr: 1, color: '#8b5cf6' }} />
                    Cheque
                  </Box>
                </MenuItem>
                <MenuItem value="tarjeta">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CardIcon sx={{ mr: 1, color: '#f59e0b' }} />
                    Tarjeta
                  </Box>
                </MenuItem>
              </MuiSelect>
            </FormControl>



            {
              (paymentMethod !== 'efectivo' && paymentMethod !== 'tarjeta') &&
              <FormControl fullWidth sx={{ mb: 2 }}>
                {banksOptions.length > 0 && (
                  <>
                    <InputLabel id="bank-label">Banco</InputLabel>
                    <MuiSelect
                      labelId="bank-label"
                      id="bank"
                      value={bank}
                      onChange={(e) => setBank(e.target.value)}
                      label="Banco"
                      fullWidth
                      MenuProps={{ disablePortal: true }}
                    >
                      {banksOptions.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.bank} - {b.accountNumber}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                    <FormHelperText>Seleccione el banco para la transferencia</FormHelperText>
                  </>
                )}
                <TextField fullWidth label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} sx={{ mb: 2 }} />
              </FormControl>
            }

            {selectedServices.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  bgcolor: 'rgba(34, 197, 94, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                }}
              >
                <MuiTypography variant="body2" sx={{ color: '#059669' }}>
                  <strong>Nota:</strong> Esta devolución se procesará según el método de pago seleccionado.
                  <br />
                  <br />
                  Verifique que los servicios seleccionados y el monto sean correctos antes de procesar.
                </MuiTypography>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MuiButton
            onClick={() => setRefundDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#dc2626',
              borderColor: '#dc2626',
              '&:hover': { borderColor: '#b91c1c', backgroundColor: 'rgba(220, 38, 38, 0.04)' }
            }}
          >
            Cancelar
          </MuiButton>
          <MuiButton
            variant="contained"
            disabled={selectedServices.length === 0 || refundAmount === 0 || loading}
            onClick={handleRefundSubmit}
            sx={{
              bgcolor: '#22c55e',
              '&:hover': { bgcolor: '#16a34a' },
              '&.Mui-disabled': { bgcolor: 'rgba(34, 197, 94, 0.3)' }
            }}
            startIcon={<RefundIcon />}
          >
            Procesar Devolución
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CreditNoteView;